import * as THREE from 'three';
import type { AntSimWorld, FoodField, NestConfig, PlayerControl } from '../sim/contracts';

interface SpiderLike { x: number; z: number }
interface SpiderSource { readonly spiders: SpiderLike[] }

interface HopperLike { x: number; z: number; alive: boolean }
interface HopperSource { readonly hoppers: HopperLike[] }

interface RolyPolyLike { x: number; z: number }
interface RolyPolySource { readonly rolies: RolyPolyLike[] }

interface LizardLike { readonly state: { alive: boolean; x: number; z: number } }

interface MowerLike {
  readonly active: boolean;
  readonly x: number;
  readonly z: number;
  readonly heading: number;
  readonly width: number;
  readonly length: number;
}

interface SideViewNode { x: number; y: number; z: number; kind: string; radius: number }
interface SideViewData {
  nodes: SideViewNode[];
  edges: Array<[number, number]>;
  playerLocal: { x: number; y: number; z: number };
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

interface MinimapOptions {
  size?: number;
  worldExtent?: number; // half-width of the world shown (metres)
  /** Additional handler fired alongside setPlayerGoal; lets main.ts fly the camera. */
  onMapClick?: (x: number, z: number) => void;
  /** Optional predator source whose positions are plotted as orange dots. */
  spiders?: SpiderSource;
  /** Optional grasshopper source plotted as lime dots. */
  grasshoppers?: HopperSource;
  /** Optional roly-poly source plotted as pink dots. */
  rolyPolies?: RolyPolySource;
  /** Optional lizard source — plotted as a larger dark-green diamond while alive. */
  lizard?: LizardLike;
  /** Optional lawnmower rectangle drawn while active. */
  mower?: MowerLike;
  /** If this returns a side-view payload, the minimap draws that instead of
   *  the surface scene. Used while the player is underground. */
  undergroundSideView?: () => SideViewData | null;
  /** Called when the player taps the sky-strip at the top of the underground
   *  minimap — a shortcut to ascend without running to the exit ring. */
  onExitUnderground?: () => void;
  /** Live 3-D preview of the nest: the minimap renders this scene from a
   *  slowly-orbiting camera each frame and blits the result onto the canvas. */
  nestPreview?: {
    scene: THREE.Scene;
    getFocus: () => { x: number; y: number; z: number } | null;
  };
}

export class Minimap {
  readonly dom: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number;
  private worldExtent: number;
  private dpr: number;

  private spiderSource: SpiderSource | null;
  private hopperSource: HopperSource | null;
  private rolySource: RolyPolySource | null;
  private lizardSource: LizardLike | null;
  private mowerSource: MowerLike | null;
  private undergroundSource: (() => SideViewData | null) | null;
  private onExitUnderground: (() => void) | null;
  private skyStripH = 0;
  private nestPreview: { scene: THREE.Scene; getFocus: () => { x: number; y: number; z: number } | null } | null = null;
  private miniRenderer: THREE.WebGLRenderer | null = null;
  private miniCamera: THREE.PerspectiveCamera | null = null;
  /** Minimap orbit state — ant-farm view is a flat side-on shot. Azimuth =
   *  π/2 puts the mini camera along +Z (matching the main scene's side view
   *  of the XY-plane excavation) and polar = π/2 keeps it level with the
   *  focus point. Wheel/pinch still adjust zoom. */
  private miniAzimuth = Math.PI * 0.5;
  private miniPolar = Math.PI * 0.5;
  private miniDistance = 20;

  constructor(
    private world: AntSimWorld & { playerId: number },
    private food: FoodField,
    private control: PlayerControl,
    private nests: NestConfig[],
    opts: MinimapOptions = {},
  ) {
    this.spiderSource = opts.spiders ?? null;
    this.hopperSource = opts.grasshoppers ?? null;
    this.rolySource = opts.rolyPolies ?? null;
    this.lizardSource = opts.lizard ?? null;
    this.mowerSource = opts.mower ?? null;
    this.undergroundSource = opts.undergroundSideView ?? null;
    this.onExitUnderground = opts.onExitUnderground ?? null;
    this.nestPreview = opts.nestPreview ?? null;
    this.size = opts.size ?? 180;
    this.worldExtent = opts.worldExtent ?? 40;
    this.dpr = Math.min(window.devicePixelRatio ?? 1, 2);
    if (this.nestPreview) {
      this.miniRenderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
      this.miniRenderer.setPixelRatio(1);
      this.miniRenderer.setSize(this.size, this.size);
      this.miniRenderer.setClearColor(0x000000, 0);
      this.miniCamera = new THREE.PerspectiveCamera(45, 1, 0.5, 200);
    }

    const c = document.createElement('canvas');
    c.width = this.size * this.dpr;
    c.height = this.size * this.dpr;
    c.style.cssText = [
      'position:fixed',
      'top:8px',
      'right:8px',
      `width:${this.size}px`,
      `height:${this.size}px`,
      'border-radius:8px',
      'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
      'background:transparent',
      'cursor:crosshair',
      'z-index:5',
    ].join(';');
    document.body.appendChild(c);
    this.dom = c;
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
    this.ctx.scale(this.dpr, this.dpr);

    const onMapClick = opts.onMapClick;
    // Drag-vs-click + orbit/zoom state for the underground preview.
    let downX = 0, downY = 0, dragging = false, active = false;
    const DRAG_THRESHOLD = 4;

    c.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.button !== 0) return;
      const rect = c.getBoundingClientRect();
      downX = e.clientX - rect.left;
      downY = e.clientY - rect.top;
      dragging = false;
      active = true;
      c.setPointerCapture(e.pointerId);
      e.stopPropagation();
    });

    c.addEventListener('pointermove', (e: PointerEvent) => {
      if (!active) return;
      const rect = c.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      if (!dragging && Math.hypot(px - downX, py - downY) > DRAG_THRESHOLD) dragging = true;
    });

    c.addEventListener('pointerup', (e: PointerEvent) => {
      if (!active) return;
      active = false;
      try { c.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      const rect = c.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      if (dragging) { e.stopPropagation(); return; }
      if (this.undergroundSource?.()) {
        if (py <= this.skyStripH && this.onExitUnderground) this.onExitUnderground();
        e.stopPropagation();
        return;
      }
      const half = this.size / 2;
      const wx = ((px - half) / half) * this.worldExtent;
      const wz = ((py - half) / half) * this.worldExtent;
      this.control.setPlayerGoal(wx, wz);
      if (onMapClick) onMapClick(wx, wz);
      e.stopPropagation();
    });

    // Wheel/pinch zoom (wheel = desktop, gesture events on iOS Safari are a
    // separate thing). Only active underground.
    c.addEventListener('wheel', (e: WheelEvent) => {
      if (!this.undergroundSource?.()) return;
      e.preventDefault();
      const factor = Math.exp(e.deltaY * 0.0015);
      this.miniDistance = Math.max(3, Math.min(60, this.miniDistance * factor));
    }, { passive: false });
  }

  draw(): void {
    const { ctx, size } = this;
    const half = size / 2;

    // Underground mode: draw a side-view 3-D projection of the nest tree.
    const underground = this.undergroundSource?.();
    if (underground) {
      this.drawUnderground(ctx, size, underground);
      return;
    }

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(15, 20, 25, 0.78)';
    ctx.fillRect(0, 0, size, size);

    // Outer boundary — square outline matching the playable extent.
    // (Old version drew a circle that fenced ants into a donut and made
    // corner nests look unreachable.)
    ctx.strokeStyle = 'rgba(180, 200, 220, 0.35)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, size - 1, size - 1);

    // Crosshair per nest: red colony = warm tan, black colony = cool grey.
    for (let i = 0; i < this.nests.length; i++) {
      const nest = this.nests[i];
      const nx = half + (nest.x / this.worldExtent) * half;
      const ny = half + (nest.z / this.worldExtent) * half;
      ctx.strokeStyle = i === 0 ? 'rgba(180, 140, 90, 0.6)' : 'rgba(150, 160, 170, 0.6)';
      ctx.beginPath();
      ctx.moveTo(nx - 4, ny); ctx.lineTo(nx + 4, ny);
      ctx.moveTo(nx, ny - 4); ctx.lineTo(nx, ny + 4);
      ctx.stroke();
    }

    const ants = this.world.ants;
    const player = ants[this.world.playerId];

    // Food piles — draw under ants so ant dots stay readable over them.
    for (const item of this.food.items) {
      if (item.amount <= 0) continue;
      const fx = half + (item.x / this.worldExtent) * half;
      const fy = half + (item.z / this.worldExtent) * half;
      // Radius grows with remaining amount so depletion is visible at a glance.
      const r = 2.5 + Math.min(4, item.amount * 0.05);
      ctx.fillStyle = 'rgba(110, 220, 120, 0.85)';
      ctx.beginPath();
      ctx.arc(fx, fy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(30, 80, 40, 0.9)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Plot normals, then followers (over normals), then player (on top).
    for (const a of ants) {
      if (!a.alive) continue;
      if (a.role !== 'normal') continue;
      const px = half + (a.x / this.worldExtent) * half;
      const py = half + (a.z / this.worldExtent) * half;
      ctx.fillStyle = a.team === 1 ? '#ffffff' : '#e63935';
      ctx.fillRect(px - 0.5, py - 0.5, 1.4, 1.4);
    }

    ctx.fillStyle = '#4dd2ff';
    for (const a of ants) {
      if (!a.alive) continue;
      if (a.role !== 'follower') continue;
      const px = half + (a.x / this.worldExtent) * half;
      const py = half + (a.z / this.worldExtent) * half;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Grasshoppers — lime dots.
    if (this.hopperSource) {
      ctx.fillStyle = '#a6ff3a';
      ctx.strokeStyle = 'rgba(20, 40, 0, 0.9)';
      ctx.lineWidth = 1;
      for (const h of this.hopperSource.hoppers) {
        if (!h.alive) continue;
        const hx = half + (h.x / this.worldExtent) * half;
        const hy = half + (h.z / this.worldExtent) * half;
        ctx.beginPath();
        ctx.arc(hx, hy, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Roly-polies — pink dots, peaceful background fauna.
    if (this.rolySource) {
      ctx.fillStyle = '#ff7fbf';
      ctx.strokeStyle = 'rgba(80, 20, 60, 0.9)';
      ctx.lineWidth = 1;
      for (const r of this.rolySource.rolies) {
        const rx = half + (r.x / this.worldExtent) * half;
        const ry = half + (r.z / this.worldExtent) * half;
        ctx.beginPath();
        ctx.arc(rx, ry, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Lizard — dark-green diamond when he's on the map. Bigger than the
    // prey dots so he reads as a genuine threat.
    if (this.lizardSource && this.lizardSource.state.alive) {
      const l = this.lizardSource.state;
      const lx = half + (l.x / this.worldExtent) * half;
      const ly = half + (l.z / this.worldExtent) * half;
      ctx.save();
      ctx.translate(lx, ly);
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = '#2e8b3a';
      ctx.strokeStyle = 'rgba(10, 30, 10, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.fillRect(-5, -5, 10, 10);
      ctx.strokeRect(-5, -5, 10, 10);
      ctx.restore();
    }

    // Spiders — bright orange dots, plotted on top of ants but under the player.
    if (this.spiderSource) {
      ctx.fillStyle = '#e91e63';
      ctx.strokeStyle = 'rgba(60, 10, 30, 0.9)';
      ctx.lineWidth = 1;
      for (const s of this.spiderSource.spiders) {
        const sx = half + (s.x / this.worldExtent) * half;
        const sy = half + (s.z / this.worldExtent) * half;
        ctx.beginPath();
        ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Mower rectangle — draw before player so the player dot sits on top.
    const mower = this.mowerSource;
    if (mower && mower.active) {
      const mx = half + (mower.x / this.worldExtent) * half;
      const my = half + (mower.z / this.worldExtent) * half;
      const lenPx = (mower.length / this.worldExtent) * half;
      const widPx = (mower.width / this.worldExtent) * half;
      ctx.save();
      ctx.translate(mx, my);
      // Convert world XZ heading to canvas Y-is-Z rotation.
      ctx.rotate(mower.heading);
      ctx.fillStyle = 'rgba(80, 150, 255, 0.85)';
      ctx.strokeStyle = 'rgba(10, 30, 80, 0.9)';
      ctx.lineWidth = 1;
      ctx.fillRect(-lenPx / 2, -widPx / 2, lenPx, widPx);
      ctx.strokeRect(-lenPx / 2, -widPx / 2, lenPx, widPx);
      ctx.restore();
    }

    // Player goal crosshair.
    if (player && player.goal) {
      const gx = half + (player.goal.x / this.worldExtent) * half;
      const gy = half + (player.goal.z / this.worldExtent) * half;
      ctx.strokeStyle = '#ff6461';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(gx - 5, gy); ctx.lineTo(gx + 5, gy);
      ctx.moveTo(gx, gy - 5); ctx.lineTo(gx, gy + 5);
      ctx.stroke();
    }

    // Player dot.
    if (player && player.alive) {
      const px = half + (player.x / this.worldExtent) * half;
      const py = half + (player.z / this.worldExtent) * half;
      ctx.fillStyle = '#ffe15c';
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(220, 220, 220, 0.45)';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText('MAP', 6, size - 6);
  }

  /** Live 3-D preview of the underground nest. Draws a daylight sky strip at
   *  the top (click to ascend), then blits a slowly-orbiting render of the
   *  actual interior meshes below. Falls back to the schematic if no mini
   *  renderer was wired in. */
  private drawUnderground(ctx: CanvasRenderingContext2D, size: number, data: SideViewData): void {
    ctx.clearRect(0, 0, size, size);
    const skyH = Math.round(size * 0.12);
    this.skyStripH = skyH;
    // Daylight sky band at the top.
    const grad = ctx.createLinearGradient(0, 0, 0, skyH);
    grad.addColorStop(0, '#6fbff0');
    grad.addColorStop(1, '#89cff0');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, skyH);
    // "Sun"-style hint + caption so the user knows it's tappable to ascend.
    ctx.fillStyle = 'rgba(255, 240, 160, 0.9)';
    ctx.beginPath();
    ctx.arc(size - 16, skyH * 0.45, skyH * 0.28, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(20, 30, 40, 0.85)';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText('Tap sky to exit', 8, Math.max(11, skyH * 0.6));
    // Surface line separating sky from underground void.
    ctx.strokeStyle = '#b6ff7a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, skyH + 0.5);
    ctx.lineTo(size, skyH + 0.5);
    ctx.stroke();
    // Underground void below.
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, skyH + 1, size, size - skyH - 1);

    // If a live preview renderer is wired in, blit a 3-D render of the nest
    // into the underground band. Much more faithful than the schematic.
    if (this.miniRenderer && this.miniCamera && this.nestPreview) {
      const focus = this.nestPreview.getFocus();
      if (focus) {
        // Ant-farm view: no auto-orbit, camera stays locked to the fixed side
        // shot. Zoom via wheel still works.
        const r = this.miniDistance;
        const sp = Math.sin(this.miniPolar);
        this.miniCamera.aspect = 1;
        this.miniCamera.position.set(
          focus.x + Math.cos(this.miniAzimuth) * r * sp,
          focus.y + Math.cos(this.miniPolar) * r,
          focus.z + Math.sin(this.miniAzimuth) * r * sp,
        );
        this.miniCamera.lookAt(focus.x, focus.y, focus.z);
        this.miniCamera.updateProjectionMatrix();
        this.miniRenderer.render(this.nestPreview.scene, this.miniCamera);
        ctx.drawImage(this.miniRenderer.domElement, 0, skyH + 1, size, size - skyH - 1);
      }

      // Overlay the player marker so you always see where you are.
      const playerX = data.playerLocal.x;
      const playerZ = data.playerLocal.z;
      const playerY = data.playerLocal.y;
      const rel = new THREE.Vector3(playerX, playerY, playerZ);
      rel.project(this.miniCamera);
      const px = (rel.x * 0.5 + 0.5) * size;
      const py = (1 - (rel.y * 0.5 + 0.5)) * (size - skyH - 1) + skyH + 1;
      ctx.fillStyle = '#ffe04a';
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = 'rgba(180, 255, 180, 0.55)';
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillText('NEST', 6, size - 6);
      return;
    }

    const { minX, maxX, minY, maxY } = data.bounds;
    const padX = 12;
    const padBottom = 12;
    const availW = size - padX * 2;
    const availH = (size - skyH - 2) - padBottom;
    const rangeX = Math.max(0.1, maxX - minX);
    const rangeY = Math.max(0.1, maxY - minY);
    const proj = (nx: number, ny: number, nz: number) => {
      const r = Math.hypot(nx, nz) * Math.sign(nx || 1);
      const cx = padX + ((r - minX) / rangeX) * availW;
      const cy = (skyH + 2) + ((maxY - ny) / rangeY) * availH;
      return { cx, cy };
    };

    // Edges — green lines for the tree structure.
    ctx.strokeStyle = '#3fcf6a';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    for (const [a, b] of data.edges) {
      const na = data.nodes[a]; const nb = data.nodes[b];
      const pa = proj(na.x, na.y, na.z);
      const pb = proj(nb.x, nb.y, nb.z);
      ctx.moveTo(pa.cx, pa.cy);
      ctx.lineTo(pb.cx, pb.cy);
    }
    ctx.stroke();

    // Nodes — dot sized by chamber radius, tinted by kind.
    for (const n of data.nodes) {
      const { cx, cy } = proj(n.x, n.y, n.z);
      const r = Math.max(2, n.radius * (availW / rangeX) * 0.25);
      if (n.kind === 'queen') ctx.fillStyle = '#b6ff7a';
      else if (n.kind === 'food') ctx.fillStyle = '#ffa030';
      else if (n.kind === 'hub' || n.kind === 'entry') ctx.fillStyle = '#7ff0a0';
      else ctx.fillStyle = '#2e8c50';
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Player marker — yellow dot.
    const p = proj(data.playerLocal.x, data.playerLocal.y, data.playerLocal.z);
    ctx.fillStyle = '#ffe04a';
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(p.cx, p.cy, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = 'rgba(180, 255, 180, 0.55)';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText('NEST', 6, size - 6);
  }
}
